/**
 * uploadFailures.ts
 *
 * What staff see when files picked for a patient's records are not uploaded,
 * shared by the Files tab, Surgery's file card and Post-ops' file card.
 */
import { getErrorCode, getErrorMessage } from '@/services/apiClient';

/** The plain copy for a failure whose own wording is not for staff. */
export const UPLOAD_FAILED_FALLBACK =
  'That file was not uploaded. Try again, and contact support if it keeps happening.';

export interface UploadFailure {
  name: string;
  reason: string;
}

/**
 * Why one file was not uploaded. A refusal (wrong type, too large) is the one
 * failure whose server wording tells staff what to change — retrying would
 * fail the same way. Any other failure's wording is internal, so it gets the
 * fallback instead.
 */
export function uploadFailureReason(err: unknown, fallback: string): string {
  return getErrorCode(err) === 'VALIDATION_ERROR' ? getErrorMessage(err, fallback) : fallback;
}

/**
 * Upload each file in turn. A refused file does not stop the ones after it;
 * every failure is collected with its reason.
 */
export async function uploadEach(
  files: File[],
  upload: (file: File, index: number) => Promise<unknown>,
  fallback: string,
): Promise<UploadFailure[]> {
  const failures: UploadFailure[] = [];
  for (let i = 0; i < files.length; i++) {
    try {
      await upload(files[i], i);
    } catch (err) {
      failures.push({ name: files[i].name, reason: uploadFailureReason(err, fallback) });
    }
  }
  return failures;
}

/**
 * One message for a pick's failures: the reason alone when one file was
 * picked, otherwise each refused file by name with its reason.
 */
export function describeUploadFailures(failures: UploadFailure[], picked: number): string {
  if (picked === 1 && failures.length === 1) return failures[0].reason;
  return failures.map((f) => `${f.name}: ${f.reason}`).join(' ');
}
